/* eslint-disable no-console */
import {
  BrowserCacheLocation,
  Configuration,
  LogLevel,
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';

import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import { environment } from 'src/environment/environment';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Object containing B2C policies for authentication.
 * @typedef {Object} b2cPolicies
 * @property {Object} names - Object containing policy names.
 * @property {string} names.signUpSignIn - Name of the sign-up/sign-in policy.
 * @property {string} names.signIn - Name of the sign-in policy.
 * @property {string} names.resetPassword - Name of the reset password policy.
 * @property {string} names.editProfile - Name of the edit profile policy.
 * @property {Object} authorities - Object containing policy authorities.
 * @property {Object} authorities.signUpSignIn - Object containing authority for sign-up/sign-in policy.
 * @property {string} authorities.signUpSignIn.authority - Authority for sign-up/sign-in policy.
 * @property {Object} authorities.signIn - Object containing authority for sign-in policy.
 * @property {string} authorities.signIn.authority - Authority for sign-in policy.
 * @property {Object} authorities.resetPassword - Object containing authority for reset password policy.
 * @property {string} authorities.resetPassword.authority - Authority for reset password policy.
 * @property {string[]} authorities.resetPassword.scopes - Scopes for reset password policy.
 * @property {Object} authorities.editProfile - Object containing authority for edit profile policy.
 * @property {string} authorities.editProfile.authority - Authority for edit profile policy.
 * @property {string} authorityDomain - Domain for policy authority.
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: environment.SIGNUP_SIGNIN_POLICY,
    signIn: environment.SIGNIN_POLICY,
    resetPassword: environment.RESET_PASSWORD_POLICY,
    editProfile: environment.EDIT_PROFILE_POLICY,
  },
  authorities: {
    signUpSignIn: {
      authority:
        environment.AZURE_AUTHORITY +
        environment.AZURE_TENANT_NAME +
        '/' +
        environment.SIGNUP_SIGNIN_POLICY,
    },
    signIn: {
      authority:
        environment.AZURE_AUTHORITY +
        environment.AZURE_TENANT_NAME +
        '/' +
        environment.SIGNIN_POLICY,
      scopes: ['openid', 'profile', 'offline_access'],
    },
    resetPassword: {
      authority:
        environment.AZURE_AUTHORITY +
        environment.AZURE_TENANT_NAME +
        '/' +
        environment.RESET_PASSWORD_POLICY,
      scopes: ['openid'],
    },
    editProfile: {
      authority:
        environment.AZURE_AUTHORITY +
        environment.AZURE_TENANT_NAME +
        '/' +
        environment.EDIT_PROFILE_POLICY,
    },
  },
  authorityDomain: environment.AZURE_AUTHORITY_DOMAIN,
};

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.AZURE_APP_CLIENT_ID,
    authority: b2cPolicies.authorities.signUpSignIn.authority,
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: environment.AZURE_REDIRECT_URI,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: isIE, // Set this to "true" if you are having issues on IE11 or Edge. Remove this line to use Angular Universal
  },
  system: {
    /**
     * Below you can configure MSAL.js logs. For more information, visit:
     * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
     */
    allowNativeBroker: false,
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {
        if (logLevel === LogLevel.Error) {
          console.error(message);
        }
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Configuration object for protected resources.
 * @typedef {Object} ProtectedResources
 * @property {Object} todoListApi - Configuration object for the Todo List API.
 * @property {string} todoListApi.endpoint - The endpoint URL for the Todo List API.
 * @property {string[]} todoListApi.scopes - The scopes required to access the Todo List API.
 */
export const protectedResources = {
  todoListApi: {
    endpoint: 'https://' + environment.AZURE_TENANT_NAME + '/test-api',
    scopes: ['https://' + environment.AZURE_TENANT_NAME + '/api/tasks.read'],
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'offline_access'],
  authority: b2cPolicies.authorities.signUpSignIn.authority,
};

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiGateway, [
    'https://' + environment.AZURE_TENANT_NAME + '/api/Web.Default',
    'openid',
  ]);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: loginRequest.scopes },
  };
}
